import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/CopyButton';
import { Skeleton } from '@/components/ui/skeleton';
import { DeleteAccountButton } from '@/components/personas/DeleteAccountButton';
import { DisconnectButton } from '@/components/personas/DisconnectButton';
import { EnterCodeField } from '@/components/personas/EnterCodeField';
import { LinkAccountForm } from '@/components/personas/LinkAccountForm';
import { ProfileSyncRow } from '@/components/personas/ProfileSyncRow';
import { ReconnectButton } from '@/components/personas/ReconnectButton';
import { RevalidateButton } from '@/components/personas/RevalidateButton';
import { ValidationStatusCard } from '@/components/personas/ValidationStatusCard';
import {
  ACCOUNT_ERROR_REASON,
  ACCOUNT_STATUS_LABEL,
  ACCOUNT_STATUS_TONE,
  ACCOUNT_VALIDATION_POLL_MS,
} from '@/constants/personas';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { Account } from '@/types/account';

/** Short, human "expires in N days" / "expired" hint from an ISO timestamp. */
function expiryHint(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'session expired';
  const days = Math.round(ms / 86_400_000);
  if (days >= 1) return `expires in ${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.max(1, Math.round(ms / 3_600_000));
  return `expires in ${hours} hour${hours === 1 ? '' : 's'}`;
}

export function AccountsPanel({
  personaId,
  accounts,
  loading,
}: {
  personaId: string;
  accounts: Account[];
  loading: boolean;
}) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [pendingWorkflowId, setPendingWorkflowId] = useState<string | null>(null);
  // Disconnected account being connected again by re-entering the password.
  const [connectingAccount, setConnectingAccount] = useState<Account | null>(null);

  // Decide the default open/closed state once the accounts have actually
  // loaded — `accounts` is empty on the first render while the query is in
  // flight, so initialising from it directly would leave the form open even
  // when a connected account exists. Auto-open only when there are none.
  const didInitForm = useRef(false);
  useEffect(() => {
    if (loading || didInitForm.current) return;
    didInitForm.current = true;
    if (accounts.length === 0) setShowForm(true);
  }, [loading, accounts.length]);

  const wfQ = useQuery({
    queryKey: ['workflow', pendingWorkflowId],
    queryFn: () => api.getWorkflow(pendingWorkflowId!),
    enabled: !!pendingWorkflowId,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && status !== 'RUNNING' ? false : ACCOUNT_VALIDATION_POLL_MS;
    },
  });

  const wfStatus = wfQ.data?.status;
  useEffect(() => {
    if (pendingWorkflowId && wfStatus && wfStatus !== 'RUNNING') {
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'accounts'] });
    }
  }, [pendingWorkflowId, wfStatus, personaId, queryClient]);

  // While a login is in flight, poll the accounts so a transition to
  // `awaiting_code` (IG asked for a verification code) surfaces promptly and the
  // inline code field appears without a manual refresh.
  const validationInFlight =
    !!pendingWorkflowId ||
    accounts.some((a) => a.status === 'pending_validation' || a.status === 'awaiting_code');
  useEffect(() => {
    if (!validationInFlight) return;
    const t = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'accounts'] });
    }, ACCOUNT_VALIDATION_POLL_MS);
    return () => clearInterval(t);
  }, [validationInFlight, personaId, queryClient]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Instagram accounts</h2>
        <Button
          variant={showForm ? 'ghost' : 'primary'}
          onClick={() => setShowForm((v) => !v)}
          disabled={!!pendingWorkflowId}
        >
          {showForm ? 'Cancel' : 'Connect Instagram account'}
        </Button>
      </div>

      {showForm && (
        <LinkAccountForm
          personaId={personaId}
          onLinked={(workflowId) => {
            setPendingWorkflowId(workflowId);
            setShowForm(false);
          }}
        />
      )}

      {connectingAccount && (
        <LinkAccountForm
          personaId={personaId}
          account={connectingAccount}
          onLinked={(workflowId) => {
            setPendingWorkflowId(workflowId);
            setConnectingAccount(null);
          }}
          onCancel={() => setConnectingAccount(null)}
        />
      )}

      {pendingWorkflowId && (
        <ValidationStatusCard
          status={wfQ.data?.status ?? 'STARTING'}
          onDismiss={() => setPendingWorkflowId(null)}
        />
      )}

      {loading ? (
        <Skeleton className="h-24" />
      ) : accounts.length === 0 && !showForm ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No accounts connected yet. Connect an Instagram account with its username and password —
            Ghost logs in for you and keeps the session.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2">
          {accounts.map((a) => (
            <Card key={a.account_id}>
              <CardContent className="space-y-3 py-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-sm font-medium">@{a.handle}</p>
                    <p className="flex items-center gap-1 font-mono text-xs text-muted-foreground">
                      <span className="truncate" title={a.account_id}>
                        {a.account_id}
                      </span>
                      <CopyButton value={a.account_id} label={`Copy ${a.account_id}`} />
                      <span className="shrink-0">· {a.platform}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end gap-0.5">
                      <Badge tone={ACCOUNT_STATUS_TONE[a.status]}>
                        {ACCOUNT_STATUS_LABEL[a.status]}
                      </Badge>
                      {a.last_error && a.status !== 'active' && (
                        <span className="text-xs text-muted-foreground">
                          {ACCOUNT_ERROR_REASON[a.last_error] ?? a.last_error.replace(/_/g, ' ')}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {a.status === 'active' && expiryHint(a.session_expires_at)
                          ? expiryHint(a.session_expires_at)
                          : a.last_validated_at
                            ? `checked ${formatRelative(a.last_validated_at)}`
                            : 'never connected'}
                      </span>
                    </div>
                    {a.status === 'awaiting_code' ? (
                      // IG asked for a verification code mid-login — the worker is
                      // holding the browser open, waiting for the operator's code.
                      <EnterCodeField accountId={a.account_id} personaId={personaId} />
                    ) : a.status === 'disconnected' ? (
                      // Disconnect keeps the stored credentials — one-click re-login.
                      <ReconnectButton accountId={a.account_id} onStarted={setPendingWorkflowId} />
                    ) : a.has_credentials ? (
                      <>
                        <RevalidateButton
                          accountId={a.account_id}
                          onStarted={setPendingWorkflowId}
                        />
                        <DisconnectButton accountId={a.account_id} personaId={personaId} />
                      </>
                    ) : (
                      // No stored credentials yet (unlinked) — enter a password to connect.
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConnectingAccount(a)}
                        disabled={connectingAccount?.account_id === a.account_id}
                      >
                        Connect
                      </Button>
                    )}
                    <DeleteAccountButton accountId={a.account_id} personaId={personaId} />
                  </div>
                </div>
                {a.status === 'active' && a.has_credentials && (
                  <ProfileSyncRow account={a} personaId={personaId} />
                )}
              </CardContent>
            </Card>
          ))}
        </ul>
      )}
    </div>
  );
}
